/**
 * Agent-facing Curva docs for MCP instructions, resources, and get_docs.
 * Markdown lives in docs/mcp/; this module is the runtime catalog.
 */

export interface McpDocTopic {
  id: string;
  title: string;
  description: string;
  fileName: string;
}

export const MCP_DOC_TOPICS: readonly McpDocTopic[] = [
  {
    id: "overview",
    title: "Curva MCP overview",
    description: "Attached session, node types, workflow, and Not-Figma basics.",
    fileName: "overview.md",
  },
  {
    id: "components",
    title: "Components and variants",
    description: "Variants are nested componentVariant children — not slash names.",
    fileName: "components.md",
  },
  {
    id: "layout",
    title: "Auto-layout",
    description: "Flex frames, centering, and text sizing patterns.",
    fileName: "layout.md",
  },
  {
    id: "svg",
    title: "SVG import",
    description: "When and how to use import_svg for icons and compound vectors.",
    fileName: "svg.md",
  },
  {
    id: "styles",
    title: "Document styles",
    description: "Shared fill / stroke / text / effects tokens via styleBindings.",
    fileName: "styles.md",
  },
  {
    id: "scripts",
    title: "Document scripts",
    description: "Luau Scripts library, frame attach, @param run values, and run_script.",
    fileName: "scripts.md",
  },
] as const;

export function mcpDocUri(topicId: string): string {
  return `curva://docs/${topicId}`;
}

export function listMcpDocTopics(): Array<{ id: string; title: string; description: string; uri: string }> {
  return MCP_DOC_TOPICS.map(topic => ({
    id: topic.id,
    title: topic.title,
    description: topic.description,
    uri: mcpDocUri(topic.id),
  }));
}

export function findMcpDocTopic(topicId: string): McpDocTopic | undefined {
  return MCP_DOC_TOPICS.find(topic => topic.id === topicId);
}

export async function readMcpDocMarkdownAsync(topicId: string): Promise<string> {
  const topic = findMcpDocTopic(topicId);
  if (!topic) {
    const known = MCP_DOC_TOPICS.map(entry => entry.id).join(", ");
    throw new Error(`Unknown docs topic "${topicId}". Known topics: ${known}.`);
  }
  // Resolve the on-disk docs path lazily and only under Bun. Keeping `node:path`
  // and `import.meta.dir` out of the module top level prevents this file (pulled
  // into the editor bundle via toolHandlers) from crashing the browser at load.
  const { join } = await import("node:path");
  const path = join(import.meta.dir, "../../docs/mcp", topic.fileName);
  const file = Bun.file(path);
  if (!(await file.exists())) {
    throw new Error(`Docs file missing for topic "${topicId}" at ${path}.`);
  }
  return file.text();
}

export async function getMcpDocs(topicId?: string): Promise<unknown> {
  if (!topicId) {
    return {
      topics: listMcpDocTopics(),
      hint: "Call get_docs with { topic } to read a guide. Start with topic \"overview\", \"components\", or \"scripts\".",
    };
  }
  const topic = findMcpDocTopic(topicId);
  if (!topic) {
    const known = MCP_DOC_TOPICS.map(entry => entry.id).join(", ");
    throw new Error(`Unknown docs topic "${topicId}". Known topics: ${known}.`);
  }
  const markdown = await readMcpDocMarkdownAsync(topicId);
  return {
    topic: topic.id,
    title: topic.title,
    uri: mcpDocUri(topic.id),
    markdown,
  };
}

/** Short always-on MCP server instructions (keep under ~25 lines). */
export function mcpServerInstructions(hostBase: string, protocolVersion: number): string {
  return [
    "Curva design editor MCP. Tools mutate the live attached editor session (what the user sees).",
    `Host protocol v${protocolVersion} on ${hostBase}. Require \`bun run mcp:dev\` + editor "Connect to MCP Host".`,
    "",
    "Not Figma: component variants are nested componentVariant children under a component — slash names mean nothing.",
    "Keep component definitions outside the main design frame; place instances in the design.",
    "Discover components/variants with list_components before insert_instance.",
    "Prefer insert_instance { parentId, x, y } (frame-relative). Use set_instance_variant to swap; duplicate_nodes to copy.",
    "Instance overrides: edit nested instance children with set_text/set_fill/etc. — no detach needed.",
    "Prefer auto-layout for UI chrome; use import_svg for icons / compound vectors; verify with export_*_png.",
    "Read guides via get_docs (topics: overview, components, layout, svg, styles, scripts) or resources curva://docs/<topic>.",
    "Typography: list_fonts → set_typography / create_text fields for one-offs; text styles for shared tokens. Font load is async before export.",
    "Use document styles (list/create/apply/update) for shared fill/stroke/text/effects tokens — prefer them over repeating raw colors.",
    "Scripts: list_scripts / get_script / create_script / attach_script / run_script (pass values for @param; never mutates source). Require Allow scripts.",
    "Use batch_tools for multi-step edits; undo/redo on mistakes.",
  ].join("\n");
}

export function mcpResourceDescriptors(): Array<{
  uri: string;
  name: string;
  description: string;
  mimeType: string;
}> {
  return MCP_DOC_TOPICS.map(topic => ({
    uri: mcpDocUri(topic.id),
    name: topic.title,
    description: topic.description,
    mimeType: "text/markdown",
  }));
}

export function topicIdFromDocUri(uri: string): string | undefined {
  const match = /^curva:\/\/docs\/([a-z0-9-]+)$/i.exec(uri.trim());
  return match?.[1];
}
